import { useState, useEffect } from "react";
import { useProfile } from "../../../hooks/useProfile";
import {
  fetchEmployeeCountAPI,
  fetchEmployeeCountsAPI,
  fetchEmployeesAPI,
  fetchArchivedEmployeesAPI,
  fetchCompaniesAPI,
  fetchClientsAPI,
} from "../services/employeeService";
import {
  mapEmployeesData,
  mapArchivedEmployeesData,
  countNewEmployees,
} from "../utils/employeeFormatters";

/**
 * 🪝 useEmployees Hook
 * Hook بسيط يجمع البيانات من service و formatters
 */
export const useEmployees = () => {
  const { profile } = useProfile();
  const clientName = profile?.clientName ?? null;
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [clientsList, setClientsList] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [counts, setCounts] = useState({
    Active: 0,
    OnLeave: 0,
    Recent: 0,
    archived: 0,
    newEmployees: 0,
    Total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const viewType = localStorage.getItem('managerViewType') || 'manager';

  // ============== FETCH ALL DATA ==============
  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch in parallel
      const [
        count,
        countsData,
        employeesData,
        archivedData,
        companiesData,
        clientsData,
      ] = await Promise.all([
        fetchEmployeeCountAPI(clientName),
        fetchEmployeeCountsAPI(viewType, clientName),
        fetchEmployeesAPI(viewType, clientName),
        fetchArchivedEmployeesAPI(clientName),
        fetchCompaniesAPI(viewType, clientName),
        fetchClientsAPI(),
      ]);

      // Map and set data
      setEmployeeCount(count);
      setCounts(countsData);
      setEmployees(mapEmployeesData(employeesData));
      setArchivedEmployees(mapArchivedEmployeesData(archivedData));
      setArchivedCount(archivedData.length);
      setCompanies(companiesData);
      setClientsMap(clientsData.map);
      setClientsList(clientsData.list);
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      setLoading(false);
    }
  };

  // ============== UPDATE COUNTS BASED ON EMPLOYEES ==============
  useEffect(() => {
    if (employees.length === 0) return;

    const newEmployeesCount = countNewEmployees(employees);
    setCounts((prev) => ({
      ...prev,
      archived: archivedCount || 0,
      newEmployees: newEmployeesCount,
      Total: (prev.Active || 0) + (prev.OnLeave || 0) + (archivedCount || 0),
    }));
  }, [archivedCount, employees]);

  // ============== INITIAL FETCH ON MOUNT ==============
  useEffect(() => {
    fetchAll();
  }, []);



  // ============== RETURN PUBLIC API ==============
  return {
    // Data
    employees,
    setEmployees,
    archivedEmployees,
    setArchivedEmployees,
    companies,
    clientsMap,
    clientsList,
    employeeCount,
    archivedCount,
    counts,
    selectedEmployee,
    
    // Setters
    setSelectedEmployee,
    setCounts,
    setEmployeeCount,
    setArchivedCount,
    
    // State
    loading,
    setLoading,
    error,
    setError,
    
    // Methods
    fetchAll,
  };
};
